import { Vector2, Vector3 } from "three";
import { PrecisionUtils } from "../../infra3d";
import { MeasurePoint, MeasureHandler } from "../measure/internal";
/**
 * Measure point representation
 * @class StereoMeasurePoint
 * @extends MeasurePoint
 * @constructor
 */
export class StereoMeasurePoint extends MeasurePoint {
    constructor(geoframe, mouseX1, mouseY1, mouseX2, mouseY2, maxdist) {
        super();
        this.mouseX1 = -1;
        this.mouseY1 = -1;
        this.mouseX2 = -1;
        this.mouseY2 = -1;
        this.offsetEasting = 0;
        this.offsetNorthing = 0;
        this.offsetHeight = 0;
        /**
         * Bildkoordinate x'
         * @property {number} imageX
         */
        this.imageX = 0.0;
        /**
         * Bildkoordinate y
         * @property {number} imageY
         */
        this.imageY = 0.0; // Bildkoordinate y'.
        this.disparity = 0.0;
        this.geoframe = geoframe;
        this.mouseX1 = mouseX1;
        this.mouseY1 = mouseY1;
        this.mouseX2 = mouseX2;
        this.mouseY2 = mouseY2;
        this.maxdist = maxdist;
        this.offsetEasting = geoframe.easting;
        this.offsetHeight = geoframe.height;
        this.offsetNorthing = geoframe.northing;
        this.calcCoordinatesFromStereo(mouseX1, mouseY1, mouseX2, mouseX2);
        this.checkCoordinates();
    }
    /**
     * Calculates the coordinates out of the mouse position
     *
     * @param {number} pixelPosLeftX - Image pixel coordinates -> NOT mouse coordinates
     * @param {number} pixelPosLeftY
     * @param {number} pixelPosRightX
     * @param {number} pixelPosRightY
     */
    calcCoordinatesFromStereo(pixelPosLeftX, pixelPosLeftY, pixelPosRightX, pixelPosRightY) {
        const mh = this.geoframe.scene.getMeasureHandler();
        const measureinfo = this.geoframe.getMeasureInfo();
        const imgcoor = mh.sensorToImage(new Vector2(pixelPosLeftX, pixelPosLeftY), this.geoframe);
        // Disparität in Tiefe Umrechnen
        this.disparityInPixel =
            Math.abs(pixelPosLeftX - pixelPosRightX) /
                (measureinfo.depthmapmeta.pixsize / measureinfo.imagemeta.pixsize);
        this.disparityInmm = this.disparityInPixel * measureinfo.depthmapmeta.pixsize * 0.001;
        this.quality = 200;
        //calculate the depth in m
        const depth = (measureinfo.depthmapmeta.focallength * measureinfo.depthmapmeta.basis) / this.disparityInmm;
        const modelcoor = mh.imageToModel(imgcoor, depth, null, this.geoframe);
        const framecoor = mh.modelToFrame(modelcoor, null, this.geoframe);
        const worldcoor = mh.frameToWorld(framecoor, this.geoframe);
        this.Easting = worldcoor.x;
        this.Northing = worldcoor.y;
        this.Height = worldcoor.z;
        //Expose imgcoor and disparityInPixel in imageX and imageY and disparity.
        //This is used for IG-Module / Systemproperties !!!!!
        this.imageX = imgcoor.x;
        this.imageY = imgcoor.y;
        this.disparity = this.disparityInPixel;
        this.frameX = framecoor.x;
        this.frameY = framecoor.y;
        this.frameZ = framecoor.z;
        this._calcPrecisionLocal();
        this._calcPrecisionLocalGeoFrame();
    }
    /**
     * returns the point values as vec3 object.
     * @return {Vector3}
     */
    getVec3() {
        const vec = new Vector3(this["Easting"], this["Northing"], this["Height"]);
        return vec;
    }
    /**
     * returns the point values as vec3 object.
     * @return {MeasurePoint}
     */
    copy() {
        const pointcopy = new StereoMeasurePoint(this.geoframe, this.mouseX1, this.mouseY1, this.mouseX2, this.mouseY2, this.maxdist);
        pointcopy.geoframe = this.geoframe;
        pointcopy.mouseX1 = this.mouseX1;
        pointcopy.mouseY1 = this.mouseY1;
        pointcopy.mouseX2 = this.mouseX2;
        pointcopy.mouseY2 = this.mouseY2;
        pointcopy.maxdist = this.maxdist;
        pointcopy["Height"] = this["Height"];
        pointcopy["Easting"] = this["Easting"];
        pointcopy["Northing"] = this["Northing"];
        return pointcopy;
    }
    /**
     * checks if the measurement is in Range
     */
    checkCoordinates() {
        this.warning = false;
        this.warntext = "";
        if (this.maxdist == null) {
            this.warning = true;
            this.warntext = "No maximal range value set !";
            return;
        }
        const d = Math.sqrt(Math.pow(this["Easting"] - this.offsetEasting, 2) +
            Math.pow(this["Northing"] - this.offsetNorthing, 2) +
            Math.pow(this["Height"] - this.offsetHeight, 2));
        this.distance = d;
        if (d > this.maxdist) {
            this.warning = true;
            this.warntext = "Position too far from projection center";
            return;
        }
        if (!this.isValid()) {
            this.warning = true;
            this.warntext = "Inavlid Values in Northing/Easting/Height";
        }
    }
    _calcPrecisionLocal() {
        const measureinfo = this.geoframe.getMeasureInfo();
        const precX = measureinfo.precDisparity * measureinfo.imagemeta.pixsize * 0.001;
        const precP = measureinfo.precDisparity * measureinfo.depthmapmeta.pixsize * 0.001;
        // NOTE: All units in [mm].
        const args = {
            x: this.imageX,
            y: this.imageY,
            p: this.disparityInmm,
            c: measureinfo.imagemeta.focallength,
            b: measureinfo.depthmapmeta.basis * 1000.0,
            precX: precX,
            precY: precX,
            precP: precP,
            precC: measureinfo.precFocallength,
            precB: measureinfo.precBasis,
        };
        const r = PrecisionUtils.pointLocal(args);
        // NOTE: All units in [m].
        this.precLocalX = r.x * 0.001;
        this.precLocalY = r.y * 0.001;
        this.precLocalZ = r.z * 0.001;
    }
    _calcPrecisionLocalGeoFrame() {
        const precLocal = new Vector3(this.precLocalX, this.precLocalY, this.precLocalZ);
        const minfo = this.geoframe.getMeasureInfo();
        const sensorpos = minfo.sensors[0].position;
        const R = MeasureHandler.getRotationMatrix((sensorpos.domega * Math.PI) / 180, (sensorpos.dphi * Math.PI) / 180, (sensorpos.dkappa * Math.PI) / 180);
        precLocal.applyMatrix4(R);
        this.precFrameX = precLocal.x;
        this.precFrameY = precLocal.y;
        this.precFrameZ = precLocal.z;
    }
}
//# sourceMappingURL=stereomeasurepoint.js.map